import { type OperationId, operationIds } from "@gen/app-access-config";
import {
  CancelF2fWithDescriptionBody,
  type CancelF2fWithDescriptionBodyOn_behalf_of,
  CoreQuery,
} from "@gen/app-query";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { ActionDialog } from "~/components/action_dialog";

interface CancelTransactionActionProps {
  canExecuteForStatus: (operationId: OperationId) => boolean;
  userTransactionRole: "buyer" | "seller";
  transactionId: number;
  isF2f?: boolean;
}

interface FormValues {
  description: string;
}

export function CancelTransactionAction({
  canExecuteForStatus,
  userTransactionRole,
  transactionId,
  isF2f,
}: CancelTransactionActionProps) {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { description: "" },
  });

  const { mutate: cancelF2fWithDescription, isPending: isCancellingF2f } =
    CoreQuery.useAdmin_cancelF2fWithDescriptionMutation(transactionId, {
      meta: {
        success: `F2F transaction cancelled by ${userTransactionRole}`,
      },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey: CoreQuery.admin_getF2fTransactionQueryKey(transactionId),
          });
        }
        setDialogOpen(false);
        reset();
      },
    });

  const { mutate: cancelOnlineTransaction, isPending: isCancellingOnline } =
    CoreQuery.useAdmin_cancelOnlineTransactionMutationWithParameters({
      meta: { success: "Online transaction cancelled" },
      onSuccess: () => {
        if (transactionId) {
          queryClient.invalidateQueries({
            queryKey:
              CoreQuery.admin_getOnlineTransactionQueryKey(transactionId),
          });
        }
        setDialogOpen(false);
        reset();
      },
    });

  const canExecute = isF2f
    ? canExecuteForStatus(operationIds["core_admin.cancelF2fWithDescription"])
    : canExecuteForStatus(operationIds["core_admin.cancelOnlineTransaction"]);

  if (!canExecute) {
    return null;
  }

  const onSubmit = ({ description }: FormValues) => {
    if (isF2f) {
      const body = CancelF2fWithDescriptionBody.fromJS({
        description: description.trim(),
        on_behalf_of:
          userTransactionRole as CancelF2fWithDescriptionBodyOn_behalf_of,
      });
      cancelF2fWithDescription(body);
    } else {
      cancelOnlineTransaction({
        transaction_id: transactionId,
        cancelOnlineTransactionBody: {},
      });
    }
  };

  const isLoading = isF2f ? isCancellingF2f : isCancellingOnline;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack flexDirection="column" spacing={5}>
        <Typography variant="body1DashedDivider">Cancel Transaction</Typography>

        {isF2f && (
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Description"
                multiline
                rows={4}
                fullWidth
                slotProps={{
                  htmlInput: {
                    "data-testid": "cancel-transaction-description-input",
                  },
                }}
              />
            )}
          />
        )}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => setDialogOpen(true)}
          data-testid="cancel-transaction-button"
        >
          Cancel transaction
        </Button>
        {isDialogOpen && (
          <ActionDialog
            heading="Cancel transaction"
            message="Are you sure you want to cancel the transaction?"
            confirmButtonMessage="Yes, cancel transaction"
            cancelButtonMessage="No, keep transaction"
            isConfirming={isLoading}
            confirm={handleSubmit(onSubmit)}
            cancel={() => {
              setDialogOpen(false);
            }}
            dataTestId="cancel-transaction"
          />
        )}
      </Stack>
    </form>
  );
}
